import { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNotification } from './useNotification'

interface OpenFilePreviewDialogParams {
  fileId: string
  fileName: string
  sectionId?: string
  sectionTitle?: string
  sectionType?: 'heading' | 'text' | 'table'
}

function useFilePreviewDialog() {
  const { t } = useTranslation()
  const { notify } = useNotification()
  // Office.js only supports one dialog at a time; ref tracks open state to show a notification
  // rather than letting the second open attempt silently fail
  const activePreviewDialogRef = useRef(false)

  const openFilePreviewDialog = useCallback(
    ({ fileId, fileName, sectionId, sectionTitle, sectionType }: OpenFilePreviewDialogParams) => {
      if (activePreviewDialogRef.current) {
        notify({ isSuccess: false, text: t('files.previewDialog.alreadyOpen'), position: 'top' })
        return
      }

      const params = new URLSearchParams({ fileId, fileName })
      if (sectionId) params.set('sectionId', sectionId)
      if (sectionTitle) params.set('sectionTitle', sectionTitle)
      if (sectionType) params.set('sectionType', sectionType)

      const dialogUrl = `${window.location.origin}/src/full-page-dialog/dialog.html?${params.toString()}`

      Office.context.ui.displayDialogAsync(dialogUrl, { width: 65, height: 85 }, (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          console.error('Preview dialog failed:', result.error.message)
          activePreviewDialogRef.current = false
          notify({ isSuccess: false, text: t('files.previewDialog.openFailed') })
          return
        }

        activePreviewDialogRef.current = true

        // Fired when the dialog is dismissed via Escape or the Office chrome X button
        result.value.addEventHandler(Office.EventType.DialogEventReceived, () => {
          activePreviewDialogRef.current = false
        })
      })
    },
    [notify, t]
  )

  return { openFilePreviewDialog }
}

export { useFilePreviewDialog }
